import {
  AllOrdersHospitalsResponse,
  AllOrdersRequestParams,
  AllOrdersResponse,
  AddTurnaroundSetsToOrderRequest,
  OrderBody,
  OrderRecordId,
  OrderResponse,
  OrdersRequestParams,
  OrdersResponse,
  CreateOrder,
  ValidateOrdersResponse,
  CreateOrdersResponse,
  ReturnOrderParams,
} from 'api/models/Orders';
import { GetSetByUssResponse, ReturnKitsRequest, ReturnKitsResponse } from 'api/models/ConfirmInDock';
import { KitInstanceId, Set, UpdateSetExpressRequest } from 'api/models/Sets';
import ApiClientBase from './ApiClientBase';
import { getAxiosOptions } from 'services/APIService';

const ORDERS_UPLOAD_REQUEST_TIMEOUT_MS = 10 * 60 * 1000; // 10min
export const ORDERS_UPLOAD_AXIOS_OPTIONS = getAxiosOptions({ timeout: ORDERS_UPLOAD_REQUEST_TIMEOUT_MS });

class OrdersEndpoint extends ApiClientBase {
  apiUri = '/api/v2/orders';

  getOrders(params: OrdersRequestParams) {
    const queryParams = { ...params };

    // Handle tenant_id which can be string or { id: string }
    const tenantParam = params.tenant_id;
    delete queryParams.tenant_id;
    if (tenantParam) {
      queryParams.tenant_id = 'string' == typeof tenantParam ? tenantParam : tenantParam.id || '';
    }

    // Covert historical param which can be 'false' or 'true' to numeric
    if (params.historical == 'false') {
      queryParams.historical = '0' as any;
    } else if (params.historical == 'true') {
      queryParams.historical = '1' as any;
    }

    const queryString = new URLSearchParams(queryParams as Record<string, string>).toString();

    return this.apiCall<OrdersResponse>({ uri: '/', data: queryString });
  }

  getAllOrders(params: AllOrdersRequestParams) {
    const queryString = new URLSearchParams(params as Record<string, string>).toString();

    return this.apiCall<AllOrdersResponse>({ uri: '/all/', data: queryString });
  }

  getAllOrdersHospitals() {
    return this.apiCall<AllOrdersHospitalsResponse>({ uri: '/all/hospitals/' });
  }

  downloadOrderCsvTemplate() {
    return this.apiCall<string>({ uri: '/orders_csv_template/' });
  }

  validateOrders(orders: OrderBody[]) {
    return this.apiCall<ValidateOrdersResponse>('/validate_orders/', 'post', orders, ORDERS_UPLOAD_AXIOS_OPTIONS);
  }

  createOrders(payload: CreateOrder[]) {
    return this.apiCall<CreateOrdersResponse>('/', 'post', payload, ORDERS_UPLOAD_AXIOS_OPTIONS);
  }

  async getOrder(orderRecordId: OrderRecordId) {
    const order = await this.apiCall<OrderResponse>({
      uri: `/${orderRecordId}/`,
      cfg: getAxiosOptions({ timeout: 60 * 1000 }), // 1 minute timeout for order details
    });

    // the implants have inspections named as inspection, rename for type Set compatibility
    if (!order?.implants) return order;

    // Implant compatibility with Set
    // I think we can remove this but we may still have implants cast to sets
    // I rather just update the BE so implants more closely matches sets
    Object.values(order.implants).forEach(sets => {
      sets.forEach(set => {
        if (set.inspection) set['inspections'] = set.inspection;
      });
    });

    return order;
  }

  addTurnaroundSetsToOrder(orderRecordId: OrderRecordId, request: AddTurnaroundSetsToOrderRequest) {
    return this.apiCall(`/${orderRecordId}/set/turnaround/`, 'post', request);
  }

  updateSetExpress(kitInstanceId: KitInstanceId, request: UpdateSetExpressRequest) {
    return this.apiCall<Set>(`/sets/${kitInstanceId}/express/`, 'put', request);
  }

  returnOrder({ orderRecordId, returnDetails, returnedByRepId }: ReturnOrderParams) {
    return this.apiCall(`/${orderRecordId}/return_order/`, 'put', {
      returnDetails,
      returnedByRepId,
    });
  }

  getSetByUss(uss: string) {
    const encodedUss = encodeURIComponent(uss);
    return this.apiCall<GetSetByUssResponse>(`/sets/return/${encodedUss}/`);
  }

  confirmReturnKits(request: ReturnKitsRequest) {
    return this.apiCall<ReturnKitsResponse>('/sets/return/', 'post', request);
  }
}

const orders = new OrdersEndpoint();

export default orders;
